import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  CONTRACT_KIND_LABEL,
  SSL_KIND_LABEL,
  alertBadgeVariant,
  daysUntil,
  formatDate,
  formatYen,
  type ExpiringData,
} from '../components/api'
import { Alert, Badge, Card, EmptyState } from '../components/ui'

export function AlertsPage() {
  const [data, setData] = useState<ExpiringData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api<ExpiringData>('/api/alerts/expiring', { query: { days } })
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [days])

  return (
    <>
      <div className="page-header">
        <div>
          <h1>期限間近</h1>
          <small className="muted">
            {data?.window_days ?? days} 日以内に期限を迎える項目を一覧表示
          </small>
        </div>
        <div className="row" style={{ gap: 4 }}>
          {[14, 30, 60, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={
                days === d ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'
              }
            >
              {d}日
            </button>
          ))}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <Card>
          <p className="muted">読み込み中…</p>
        </Card>
      ) : (
        <>
          <Section
            title={`ドメイン期限（${data?.domains.length ?? 0}）`}
            emptyTitle="期限間近のドメインはありません"
            empty={!data || data.domains.length === 0}
          >
            <table className="table">
              <thead>
                <tr>
                  <th>顧客</th>
                  <th>ドメイン</th>
                  <th>期限</th>
                  <th style={{ width: 100 }}>残日数</th>
                </tr>
              </thead>
              <tbody>
                {data?.domains.map((d) => (
                  <Row
                    key={d.id}
                    customerId={d.customer_id}
                    customerName={d.customer_name}
                    label={<span className="mono">{d.domain}</span>}
                    expiresAt={d.expires_at}
                  />
                ))}
              </tbody>
            </table>
          </Section>

          <Section
            title={`SSL 期限（${data?.ssl.length ?? 0}）`}
            emptyTitle="期限間近の SSL はありません"
            empty={!data || data.ssl.length === 0}
          >
            <table className="table">
              <thead>
                <tr>
                  <th>顧客</th>
                  <th>SSL（ドメイン）</th>
                  <th>期限</th>
                  <th style={{ width: 100 }}>残日数</th>
                </tr>
              </thead>
              <tbody>
                {data?.ssl.map((s) => (
                  <Row
                    key={s.id}
                    customerId={s.customer_id}
                    customerName={s.customer_name}
                    label={
                      <>
                        {SSL_KIND_LABEL[s.kind]}{' '}
                        <span className="subtle mono">／ {s.domain}</span>
                      </>
                    }
                    expiresAt={s.expires_at}
                  />
                ))}
              </tbody>
            </table>
          </Section>

          <Section
            title={`サーバー契約期限（${data?.servers.length ?? 0}）`}
            emptyTitle="期限間近のサーバー契約はありません"
            empty={!data || data.servers.length === 0}
          >
            <table className="table">
              <thead>
                <tr>
                  <th>顧客</th>
                  <th>サーバー種別</th>
                  <th>期限</th>
                  <th style={{ width: 100 }}>残日数</th>
                </tr>
              </thead>
              <tbody>
                {data?.servers.map((s) => (
                  <Row
                    key={s.id}
                    customerId={s.customer_id}
                    customerName={s.customer_name}
                    label={s.kind}
                    expiresAt={s.expires_at}
                  />
                ))}
              </tbody>
            </table>
          </Section>

          <Section
            title={`保守契約期限（${data?.contracts.length ?? 0}）`}
            emptyTitle="期限間近の保守契約はありません"
            empty={!data || data.contracts.length === 0}
          >
            <table className="table">
              <thead>
                <tr>
                  <th>顧客</th>
                  <th>契約種別</th>
                  <th className="text-right">月額</th>
                  <th>終了日</th>
                  <th style={{ width: 100 }}>残日数</th>
                </tr>
              </thead>
              <tbody>
                {data?.contracts.map((c) => {
                  const days = daysUntil(c.expires_at)
                  return (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/customers/${c.customer_id}`}>
                          {c.customer_name}
                        </Link>
                      </td>
                      <td>{CONTRACT_KIND_LABEL[c.kind]}</td>
                      <td className="text-right">
                        {formatYen(c.monthly_amount)}
                      </td>
                      <td>{formatDate(c.expires_at)}</td>
                      <td>
                        <Badge variant={alertBadgeVariant(days)}>
                          {days === null
                            ? '—'
                            : days >= 0
                              ? `あと${days}日`
                              : `${-days}日経過`}
                        </Badge>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </>
  )
}

function Section({
  title,
  emptyTitle,
  empty,
  children,
}: {
  title: string
  emptyTitle: string
  empty: boolean
  children: ReactNode
}) {
  return (
    <Card title={title}>
      {empty ? <EmptyState title={emptyTitle} /> : children}
    </Card>
  )
}

function Row({
  customerId,
  customerName,
  label,
  expiresAt,
}: {
  customerId: number
  customerName: string
  label: ReactNode
  expiresAt: string
}) {
  const days = daysUntil(expiresAt)
  return (
    <tr>
      <td>
        <Link to={`/customers/${customerId}`}>{customerName}</Link>
      </td>
      <td>{label}</td>
      <td>{formatDate(expiresAt)}</td>
      <td>
        <Badge variant={alertBadgeVariant(days)}>
          {days === null
            ? '—'
            : days >= 0
              ? `あと${days}日`
              : `${-days}日経過`}
        </Badge>
      </td>
    </tr>
  )
}
